import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  FormControl,
  Select,
  InputLabel,
  Stack,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  Block as BlockIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  CheckCircle,
} from '@mui/icons-material';
import { useAdminUsers } from '../../hooks/admin/useAdminUsers';
import type { User } from '../../hooks/admin/useAdminUsers';
import { useNavigate } from 'react-router-dom';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  dangerBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
  editorialDialogTitleSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  KPIStrip,
  KPI,
  FilterBar,
  LedgerCard,
  ActionIcon,
  StatusDot,
  MonoTag,
  EmptyState,
  ledgerTableHeadSx,
  ledgerRowSx,
  ledgerPaginationSx,
  ledgerMenuPaperSx,
  HeaderIconBtn,
} from '../../components/admin/LedgerPrimitives';

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all'); // eslint-disable-line @typescript-eslint/no-unused-vars

  const {
    users,
    stats,
    totalUsers,
    isLoading,
    isLoadingStats,
    refetch,
    deleteUser,
    toggleAdmin,
    toggleActiveStatus,
  } = useAdminUsers({
    page: page + 1,
    per_page: rowsPerPage,
    search: searchTerm.length > 0 ? searchTerm : undefined,
    is_active: statusFilter !== 'all' ? statusFilter === 'active' : undefined,
    is_admin: selectedRole !== 'all' ? selectedRole === 'admin' : undefined,
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };
  const handleRoleFilterChange = (event: SelectChangeEvent) => {
    setSelectedRole(event.target.value);
    setPage(0);
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };
  const handleCloseDeleteDialog = () => setOpenDeleteDialog(false);

  const handleDeleteUser = async () => {
    if (selectedUser) {
      try {
        await deleteUser(selectedUser.id);
        handleCloseDeleteDialog();
      } catch (err) {
        console.error(err);
      }
    }
  };
  const handleViewUser = (userId: number) => navigate(`/admin/users/${userId}`);
  const handleExportUsers = () => console.log('Exporting users…');

  const handleToggleUserAdmin = async (userId: number) => {
    try {
      await toggleAdmin(userId);
      handleMenuClose();
    } catch (err) {
      console.error(err);
    }
  };
  const handleToggleUserActive = async (userId: number, currentStatus: boolean) => {
    try {
      await toggleActiveStatus(userId, currentStatus);
      handleMenuClose();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    refetch(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm, statusFilter, selectedRole, verifiedFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <LedgerPage eyebrowFooter={`Almaship · Users · ${String(totalUsers).padStart(4, '0')} records`}>
      <PageHeader
        eyebrow="Admin · Directory"
        title={
          <>
            Users
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              .
            </Box>
          </>
        }
        description="Accounts, roles, and verification across the platform."
        actions={
          <>
            <HeaderIconBtn title="Refresh" onClick={() => refetch()}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </HeaderIconBtn>
            <Button onClick={handleExportUsers} startIcon={<DownloadIcon sx={{ fontSize: 16 }} />} sx={ghostBtnSx}>
              Export
            </Button>
            <Button
              onClick={() => navigate('/admin/users/add')}
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={primaryBtnSx}
            >
              New user
            </Button>
          </>
        }
      />

      <KPIStrip cols={5}>
        <KPI label="Total" value={isLoadingStats ? '—' : stats.total_users} sub="all accounts" />
        <KPI label="Active" value={isLoadingStats ? '—' : stats.active_users} sub="signed-in OK" accent />
        <KPI label="Inactive" value={isLoadingStats ? '—' : stats.inactive_users} sub="paused" />
        <KPI label="Admins" value={isLoadingStats ? '—' : stats.admins} sub="elevated" />
        <KPI label="Verified" value={isLoadingStats ? '—' : stats.verified_users} sub="email confirmed" />
      </KPIStrip>

      <FilterBar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or email…"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: INK.subtle }} />
                </InputAdornment>
              ),
            }}
            sx={ledgerInputSx}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel
            sx={{
              fontFamily: FONTS.sans,
              fontSize: 13,
              color: INK.muted,
              '&.Mui-focused': { color: INK.accent },
            }}
          >
            Status
          </InputLabel>
          <Select value={statusFilter} label="Status" onChange={handleStatusFilterChange} sx={ledgerSelectSx}>
            <MenuItem value="all" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>All status</MenuItem>
            <MenuItem value="active" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Active</MenuItem>
            <MenuItem value="inactive" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Inactive</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel
            sx={{
              fontFamily: FONTS.sans,
              fontSize: 13,
              color: INK.muted,
              '&.Mui-focused': { color: INK.accent },
            }}
          >
            Role
          </InputLabel>
          <Select value={selectedRole} label="Role" onChange={handleRoleFilterChange} sx={ledgerSelectSx}>
            <MenuItem value="all" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>All roles</MenuItem>
            <MenuItem value="admin" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>Admin</MenuItem>
            <MenuItem value="user" sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>User</MenuItem>
          </Select>
        </FormControl>
      </FilterBar>

      <LedgerCard padded={false}>
        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={ledgerTableHeadSx}>
                <TableCell sx={{ width: 56 }}>№</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Verified</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 10, borderBottom: 'none' }}>
                    <CircularProgress size={28} sx={{ color: INK.accent }} />
                  </TableCell>
                </TableRow>
              ) : users.length > 0 ? (
                users.map((user) => (
                  <TableRow key={user.id} sx={ledgerRowSx}>
                    <TableCell>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text, fontWeight: 500 }}>
                        {String(user.id).padStart(3, '0')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: 13.5, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                        {user.first_name} {user.last_name}
                      </Typography>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10.5, color: INK.subtle, letterSpacing: '0.04em' }}>
                        ID · {user.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.text }}>{user.email}</Typography>
                      {user.phone && (
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle, mt: 0.25 }}>
                          {user.phone}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text }}>
                      {user.country?.name || <Box component="span" sx={{ color: INK.faint }}>—</Box>}
                    </TableCell>
                    <TableCell>
                      <StatusDot
                        label={user.is_active ? 'Active' : 'Paused'}
                        tone={user.is_active ? 'active' : 'inactive'}
                      />
                    </TableCell>
                    <TableCell>
                      <MonoTag accent={user.is_admin}>{user.is_admin ? 'Admin' : 'User'}</MonoTag>
                    </TableCell>
                    <TableCell>
                      <StatusDot
                        label={user.is_verified ? 'Verified' : 'Unverified'}
                        tone={user.is_verified ? 'info' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted }}>
                        {new Date(user.created_at).toLocaleDateString('en-CA')}
                      </Typography>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.faint, mt: 0.25, letterSpacing: '0.1em' }}>
                        {new Date(user.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack className="row-actions" direction="row" spacing={0.5} justifyContent="flex-end" sx={{ opacity: 0.45, transition: 'opacity 0.15s' }}>
                        <ActionIcon title="View" onClick={() => handleViewUser(user.id)}>
                          <ViewIcon sx={{ fontSize: 15 }} />
                        </ActionIcon>
                        <ActionIcon title="Edit" onClick={() => handleViewUser(user.id)}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </ActionIcon>
                        <ActionIcon title="More" onClick={(e) => handleMenuOpen(e, user)}>
                          <MoreVertIcon sx={{ fontSize: 15 }} />
                        </ActionIcon>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} sx={{ borderBottom: 'none' }}>
                    <EmptyState
                      icon={<SearchIcon sx={{ fontSize: 22 }} />}
                      title="No users on record."
                      description="Try adjusting your search or filter."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={ledgerPaginationSx}
        />
      </LedgerCard>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{ sx: { ...ledgerMenuPaperSx, minWidth: 200 } }}
      >
        <MenuItem onClick={() => { if (selectedUser) handleViewUser(selectedUser.id); handleMenuClose(); }}>
          <ListItemIcon><PersonIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="View profile" />
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => selectedUser && handleToggleUserAdmin(selectedUser.id)}>
          <ListItemIcon><AdminIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary={selectedUser?.is_admin ? 'Remove admin' : 'Make admin'} />
        </MenuItem>
        <MenuItem onClick={() => selectedUser && handleToggleUserActive(selectedUser.id, selectedUser.is_active)}>
          <ListItemIcon>
            {selectedUser?.is_active ? <BlockIcon sx={{ fontSize: 16 }} /> : <CheckCircle sx={{ fontSize: 16 }} />}
          </ListItemIcon>
          <ListItemText primary={selectedUser?.is_active ? 'Deactivate' : 'Activate'} />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: `${INK.inactive} !important`, '& .MuiListItemIcon-root': { color: `${INK.inactive} !important` } }}>
          <ListItemIcon><DeleteIcon sx={{ fontSize: 16 }} /></ListItemIcon>
          <ListItemText primary="Delete" />
        </MenuItem>
      </Menu>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        PaperProps={{ sx: { borderRadius: 0.5 } }}
      >
        <DialogTitle sx={editorialDialogTitleSx}>Delete user</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontFamily: FONTS.sans, color: INK.muted, mt: 2 }}>
            Are you sure you want to delete this user? This action cannot be undone and all associated data will be permanently removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleCloseDeleteDialog} sx={ghostBtnSx}>Cancel</Button>
          <Button onClick={handleDeleteUser} sx={dangerBtnSx} startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </LedgerPage>
  );
};

export default UsersPage;
